<script setup lang="ts">
import { computed, ref, useId, watch } from "vue";

import {
  getDuplicateTargetDays,
  isDuplicateTargetGroupSelected,
  PROGRAM_DAYS,
  PROGRAM_WEEKEND_DAYS,
  PROGRAM_WORKING_DAYS,
  toggleDuplicateTargetDay,
  toggleDuplicateTargetGroup,
} from "@/components/programming/weekly-program/duplicate-day-program";
import { useTypedIntl } from "@/locales/typed-intl";
import { EDay } from "@/utils/date";
import { cn } from "@/utils/libs/tailwind-utils";

import Dialog from "primevue/dialog";

import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";

interface IProps {
  sourceDay: EDay;
  submitAction: (targetDays: EDay[]) => Promise<boolean>;
  loading?: boolean;
}

const visible = defineModel<boolean>({ default: false });
const props = withDefaults(defineProps<IProps>(), {
  loading: false,
});

const { t, k } = useTypedIntl();
const titleId = useId();
const subtitleId = useId();
const selectedDays = ref<EDay[]>([]);
const targetDays = computed(() => getDuplicateTargetDays(props.sourceDay));

watch(
  visible,
  (isVisible) => {
    if (isVisible) selectedDays.value = [];
  },
  { immediate: true },
);

function isSelected(day: EDay): boolean {
  return selectedDays.value.includes(day);
}

function isGroupSelected(groupDays: readonly EDay[]): boolean {
  return isDuplicateTargetGroupSelected(selectedDays.value, groupDays, props.sourceDay);
}

function toggleDay(day: EDay) {
  selectedDays.value = toggleDuplicateTargetDay(selectedDays.value, day);
}

function toggleGroup(groupDays: readonly EDay[]) {
  selectedDays.value = toggleDuplicateTargetGroup(selectedDays.value, groupDays, props.sourceDay);
}

function cancel() {
  if (props.loading) return;

  visible.value = false;
}

async function submit() {
  if (!selectedDays.value.length || props.loading) return;

  try {
    if (await props.submitAction([...selectedDays.value])) {
      visible.value = false;
    }
  } catch {}
}
</script>

<template>
  <Dialog
    v-model:visible="visible"
    class="w-[520px] overflow-hidden !rounded-xl"
    :closable="!props.loading"
    :close-on-escape="!props.loading"
    :draggable="false"
    modal
    :pt="{
      root: { 'aria-labelledby': titleId, 'aria-describedby': subtitleId },
      header: { class: '!items-start !px-6 !pb-5 !pt-6' },
      content: { class: '!px-6 !pb-0' },
      footer: { class: '!px-6 !pb-6 !pt-8' },
    }"
  >
    <template #header>
      <div class="flex min-w-0 flex-1 flex-col gap-1 pr-8">
        <h2
          :id="titleId"
          class="text-lg font-semibold leading-normal text-text-primary"
        >
          {{
            t(
              k.control.programming.details.dayDuplication.title({
                day: t(k.global.weekDays[props.sourceDay]),
              }),
            )
          }}
        </h2>
        <p
          :id="subtitleId"
          class="text-base font-normal leading-normal text-text-secondary"
        >
          {{ t(k.control.programming.details.dayDuplication.subtitle) }}
        </p>
      </div>
    </template>

    <div class="flex flex-col gap-3">
      <div class="flex flex-wrap items-center gap-2">
        <button
          :aria-pressed="isGroupSelected(PROGRAM_DAYS)"
          :class="
            cn('duplicate-day-shortcut', {
              'duplicate-day-shortcut--selected': isGroupSelected(PROGRAM_DAYS),
            })
          "
          data-test-id="duplicate-day-shortcut-all"
          type="button"
          @click="toggleGroup(PROGRAM_DAYS)"
        >
          {{ t(k.control.programming.details.dayDuplication.allDays) }}
        </button>
        <button
          :aria-pressed="isGroupSelected(PROGRAM_WORKING_DAYS)"
          :class="
            cn('duplicate-day-shortcut', {
              'duplicate-day-shortcut--selected': isGroupSelected(PROGRAM_WORKING_DAYS),
            })
          "
          data-test-id="duplicate-day-shortcut-working"
          type="button"
          @click="toggleGroup(PROGRAM_WORKING_DAYS)"
        >
          {{ t(k.control.programming.details.dayDuplication.workingDays) }}
        </button>
        <button
          :aria-pressed="isGroupSelected(PROGRAM_WEEKEND_DAYS)"
          :class="
            cn('duplicate-day-shortcut', {
              'duplicate-day-shortcut--selected': isGroupSelected(PROGRAM_WEEKEND_DAYS),
            })
          "
          data-test-id="duplicate-day-shortcut-weekend"
          type="button"
          @click="toggleGroup(PROGRAM_WEEKEND_DAYS)"
        >
          {{ t(k.control.programming.details.dayDuplication.weekend) }}
        </button>
      </div>

      <div class="flex flex-col gap-2">
        <button
          v-for="day in targetDays"
          :key="day"
          :aria-pressed="isSelected(day)"
          :class="
            cn('duplicate-day-target', {
              'duplicate-day-target--selected': isSelected(day),
            })
          "
          :data-test-id="`duplicate-day-target-${day}`"
          type="button"
          @click="toggleDay(day)"
        >
          {{ t(k.global.weekDays[day]) }}
          <span
            aria-hidden="true"
            :class="
              cn('duplicate-day-target__indicator', {
                'duplicate-day-target__indicator--selected': isSelected(day),
              })
            "
          >
            <i
              v-if="isSelected(day)"
              class="pi pi-check !text-[10px]"
              data-test-id="duplicate-day-target-check"
            />
          </span>
        </button>
      </div>
    </div>

    <template #footer>
      <div class="grid w-full grid-cols-2 gap-3">
        <HospitalityButton
          data-test-id="duplicate-day-cancel"
          :disabled="props.loading"
          :label="t(k.global.misc.cancel)"
          severity="secondary"
          @click="cancel"
        />
        <HospitalityButton
          data-test-id="duplicate-day-submit"
          :disabled="!selectedDays.length || props.loading"
          :label="t(k.global.misc.submit)"
          :loading="props.loading"
          @click="submit"
        />
      </div>
    </template>
  </Dialog>
</template>

<style lang="scss">
.duplicate-day-shortcut {
  @apply flex items-center gap-2 rounded-full border px-3 py-2 text-sm font-semibold leading-normal transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand;

  &:not(.duplicate-day-shortcut--selected) {
    @apply border-neutral-light bg-white text-text-secondary hover:border-brand hover:text-brand;
  }

  &--selected {
    @apply border-brand bg-brand-soft text-brand hover:bg-[#dcecff];
  }
}

.duplicate-day-target {
  @apply flex w-full items-center justify-between rounded-2xl border p-3 text-base font-semibold leading-normal transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand;

  &:not(.duplicate-day-target--selected) {
    @apply border-neutral-light bg-white text-text-primary hover:border-border-subtle;
  }

  &--selected {
    @apply border-[#4c9fff] bg-brand-soft font-bold text-brand;
  }

  &__indicator {
    @apply flex size-4 shrink-0 items-center justify-center rounded-full border border-border-subtle bg-white;

    &--selected {
      @apply border-brand bg-brand text-white;
    }
  }
}
</style>
